import * as Sentry from '@sentry/angular';

import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';

import { BehaviorSubject, Subscription } from 'rxjs';

import { AuthService, OrganisationService, PersonalTaxService } from '@core/services';
import { AppService } from '@core/services/app.service';
import { StringInterpolationService } from '@core/services/string-interpolation.service';

import { environment } from '@env';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false,
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
    loading: BehaviorSubject<boolean>;
    subscriptions = [] as Subscription[];
    private observer?: MutationObserver;

    constructor(
        private appService: AppService,
        private authService: AuthService,
        private organisationService: OrganisationService,
        private personalTaxService: PersonalTaxService,
        private interpolationService: StringInterpolationService,
    ) {
        this.appService.loading.next(true);
        this.loading = this.appService.loading;
        this.subscriptions.push(
            this.organisationService.activeOrganisation.subscribe((org) => {
                this.interpolationService.updateOrganisationInterpolations(org);
            }),
        );
        this.subscriptions.push(
            this.personalTaxService.activePersonalTaxFile.subscribe((taxFile) => {
                this.interpolationService.updateTaxInterpolations(taxFile);
            }),
        );
    }

    ngOnInit(): void {
        this.authService.restoreSession().subscribe({
            next: (success) => {
                if (success) {
                    console.debug('Session valid');
                    this.appService.loading.next(false);
                } else {
                    if (!this.authService.isRefreshTokenExpired()) {
                        console.debug('Session no longer valid, trying to refresh');
                        this.authService.refreshAccessToken().subscribe({
                            next: (refreshSuccess) => {
                                if (refreshSuccess) {
                                    console.debug('Access token refreshed successfully!');
                                    this.authService.getTokenUser().subscribe({
                                        next: () => {
                                            console.debug('User successfully set');
                                            this.appService.loading.next(false);
                                        },
                                        error: (error) => {
                                            console.debug('User could not be set: ', error);
                                            this.logout();
                                        },
                                    });
                                } else {
                                    console.debug('Access token refresh failed, logging out');
                                    this.logout();
                                }
                            },
                            error: (error) => {
                                console.error('Error while refreshing access token: ', error);
                                this.logout();
                            },
                        });
                    } else {
                        console.debug('Session expired, logging out');
                        this.logout();
                    }
                }
            },
            error: (error) => {
                console.error('Error while restoring session: ', error);
                this.logout();
            },
        });
    }

    ngAfterViewInit(): void {
        // Select the node that will be observed for mutations
        const targetNode = document.getElementById('freshworks-container')?.childNodes?.[0];
        // Options for the observer (which mutations to observe)
        const config = { attributes: false, childList: true, subtree: false };

        // Callback function to execute when mutations are observed
        const callback = (mutationList: MutationRecord[], observer: MutationObserver) => {
            if (mutationList.some((mutation) => (mutation.addedNodes[0] as any)?.id == 'freshworks-frame-wrapper')) {
                setTimeout(() => {
                    const user = this.authService.activeUser.getValue();
                    const Widget = (window as any).FreshworksWidget;
                    const sessionId = Sentry.getReplay()?.getReplayId();
                    if (Widget) {
                        Widget('prefill', 'ticketForm', {
                            'name': user ? `${user?.firstName} ${user?.lastName}` : '',
                            'email': user?.email ?? '',
                            'custom_fields': {
                                'cf_sentry_url': environment.getSentryReplayUrl(sessionId),
                            },
                        });
                        Widget('hide', 'ticketForm', ['custom_fields.cf_sentry_url']);
                    }
                    const freshworksIframeContent = (document.getElementById('widget-frame') as any)?.contentDocument;
                    freshworksIframeContent?.addEventListener('click', (event: any) => this.flushSentryOnFormSubmission(event));
                    freshworksIframeContent?.addEventListener('touchstart', (event: any) => this.flushSentryOnFormSubmission(event));
                });
            }
        };
        // Create an observer instance linked to the callback function
        this.observer = new MutationObserver(callback);
        // Start observing the target node for configured mutations
        if (targetNode) {
            this.observer.observe(targetNode, config);
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
        this.observer?.disconnect();
    }

    flushSentryOnFormSubmission(event: any) {
        if (event.target.id === 'form-button') {
            Sentry.getReplay()?.flush();
        }
    }

    /**
     * Send the user back to the login page, clearing the stored session, and saving the current route if necessary.
     **/
    private logout(): void {
        this.authService.clearSessionTokens();
        this.appService.loading.next(false);
    }
}
